/**
 * Schoolhouse domain types, runtime guards, mappers, and fetch helpers.
 *
 * Module: lib/schoolhouse
 * Layer:  util / data-access
 *
 * Purpose:
 *     Defines the public schoolhouse shapes consumed by the /schoolhouse
 *     browse + detail pages, validates every `/schoolhouse/*` response at the
 *     network boundary with hand-written runtime guards (the project does not
 *     declare zod — see lib/daily.ts for the same convention), and maps the
 *     backend's `SchoolhousePublicResponse` / classroom / tier payloads onto
 *     the leaner view-model shapes the UI renders. The backend mounts these
 *     routes under the bare `/schoolhouse` prefix (no `/api`), so the paths
 *     here intentionally differ from the `/api/courses` family.
 *
 * AI context:
 *     - Entry points called by: src/app/schoolhouse/** pages, their colocated
 *       hooks, and src/components/schoolhouse/SchoolhouseCard.tsx.
 *     - Depends on: the browser `fetch` API only (matches lib/daily.ts).
 *     - Side effects: network I/O via the fetch helpers.
 *     - Invariants: a resolved value always passed its runtime guard; list
 *       reads return `[]` and singletons return `null` on any non-OK or
 *       malformed response, so callers render empty/not-found states instead
 *       of crashing. Mutations (enroll) surface failures by returning `null`.
 *     - Replacement cost: LOW.
 *
 * Threat model:
 *     - Responses are untrusted JSON. The `is*` guards are the trust boundary:
 *       only objects with the exact expected primitive shape survive, so the UI
 *       never renders attacker-controlled object keys. Decimal money fields are
 *       serialized by FastAPI as JSON strings and coerced through a single
 *       guarded numeric parser.
 */

// --- Section: View-model types ---

/** Summary card shape for the schoolhouse browse grid. */
export interface SchoolhouseSummary {
  readonly id: number;
  readonly slug: string;
  readonly name: string;
  readonly tagline: string | null;
  readonly description: string | null;
  readonly cover_image_url: string | null;
  readonly teacher_name: string;
  readonly classroom_count: number;
  readonly student_count: number;
  readonly rating: number | null;
  readonly is_featured: boolean;
}

/** A bookable classroom shown on the detail page. */
export interface SchoolhouseClassroom {
  readonly id: number;
  readonly name: string;
  readonly description: string | null;
  readonly max_students: number;
  readonly current_enrollment: number;
  readonly schedule_summary: string;
}

/** A subscription tier shown on the detail page. */
export interface SchoolhouseTier {
  readonly id: number;
  readonly name: string;
  readonly price_monthly: number;
  readonly features: readonly string[];
}

/** Full detail shape combining the schoolhouse, its classrooms, and its tiers. */
export interface SchoolhouseDetail extends SchoolhouseSummary {
  readonly classrooms: readonly SchoolhouseClassroom[];
  readonly tiers: readonly SchoolhouseTier[];
}

/** A learner's classroom enrollment (subset of the backend response). */
export interface SchoolhouseEnrollment {
  readonly id: number;
  readonly classroom_id: number;
  readonly status: string;
}

/** Sort keys accepted by the backend browse endpoint (data-driven). */
export type SchoolhouseSortBy = 'featured' | 'rating' | 'students' | 'recent';

/** Optional filters for {@link fetchSchoolhouses}. */
export interface SchoolhouseBrowseParams {
  readonly search?: string;
  readonly featuredOnly?: boolean;
  readonly sortBy?: SchoolhouseSortBy;
}

// --- Section: Runtime guards ---

/** The primitive types a guarded field may hold. */
type FieldType = 'string' | 'number' | 'boolean';

/** Narrow an unknown value to a non-null record. */
function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  // WHY: arrays are objects too; reject them so a JSON array never masquerades
  // as an entity record and slips a numeric index through a field check.
  return Array.isArray(value) ? null : (value as Record<string, unknown>);
}

/**
 * Verify a field->type table against a record (data-driven boundary check).
 *
 * @param row - The candidate record.
 * @param fields - Field-name -> expected primitive type table.
 * @returns `true` when every listed field has its expected primitive type.
 * @complexity O(n) over the field table.
 */
function fieldsMatch(
  row: Record<string, unknown>,
  fields: Readonly<Record<string, FieldType>>,
): boolean {
  return Object.entries(fields).every(([name, expected]) => typeof row[name] === expected);
}

/** Parse a non-empty numeric string to a finite number, else `null`. */
function parseNumericString(value: string): number | null {
  if (value.trim() === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Coerce a nullable string|number|null money/rating field to `number | null`. */
function toNullableNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  // WHY: FastAPI serializes Decimal as a JSON string (e.g. "29.99"); parse it
  // through a single guarded path so the UI only ever sees a finite number.
  return typeof value === 'string' ? parseNumericString(value) : null;
}

/** Coerce a required money field to a non-negative number (0 on any gap). */
function toNumber(value: unknown): number {
  const parsed = toNullableNumber(value);
  return parsed === null || parsed < 0 ? 0 : parsed;
}

/** Read a nullable string field, mapping missing/non-string to `null`. */
function toNullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

/** Field schema shared by the public-response identity fields. */
const PUBLIC_FIELDS: Readonly<Record<string, FieldType>> = {
  id: 'number',
  name: 'string',
  slug: 'string',
  is_featured: 'boolean',
};

/** Type guard for the backend `SchoolhousePublicResponse` core shape. */
function isPublicSchoolhouse(value: unknown): value is Record<string, unknown> {
  const row = asRecord(value);
  return row !== null && fieldsMatch(row, PUBLIC_FIELDS);
}

/** Field schema for the backend `SchoolhouseClassroomResponse` core shape. */
const CLASSROOM_FIELDS: Readonly<Record<string, FieldType>> = {
  id: 'number',
  name: 'string',
  slug: 'string',
  schedule_type: 'string',
  current_enrollment: 'number',
};

/** Type guard for a single classroom response row. */
function isClassroomRow(value: unknown): value is Record<string, unknown> {
  const row = asRecord(value);
  return row !== null && fieldsMatch(row, CLASSROOM_FIELDS);
}

/** Field schema for the backend `SchoolhouseSubscriptionTierResponse` core. */
const TIER_FIELDS: Readonly<Record<string, FieldType>> = {
  id: 'number',
  name: 'string',
  slug: 'string',
};

/** Type guard for a single tier response row. */
function isTierRow(value: unknown): value is Record<string, unknown> {
  const row = asRecord(value);
  return row !== null && fieldsMatch(row, TIER_FIELDS);
}

/** Field schema for the backend `SchoolhouseEnrollmentResponse` core. */
const ENROLLMENT_FIELDS: Readonly<Record<string, FieldType>> = {
  id: 'number',
  classroom_id: 'number',
  status: 'string',
};

/** Type guard for a single enrollment response row. */
function isEnrollmentRow(value: unknown): value is Record<string, unknown> {
  const row = asRecord(value);
  return row !== null && fieldsMatch(row, ENROLLMENT_FIELDS);
}

// --- Section: Mappers ---

/** Extract the public teacher display name, defaulting when absent. */
function teacherName(value: unknown): string {
  const teacher = asRecord(value);
  const name = teacher === null ? null : toNullableString(teacher['name']);
  return name ?? 'Teacher';
}

/**
 * Map a guarded public-response record onto a {@link SchoolhouseSummary}.
 *
 * @param row - A record that already passed {@link isPublicSchoolhouse}.
 * @returns The leaner summary view-model the cards render.
 * @remarks `classroom_count` is absent from the browse payload, so it
 *   defaults to 0; the detail page supplies the real classroom list.
 */
function toSummary(row: Record<string, unknown>): SchoolhouseSummary {
  return {
    id: row['id'] as number,
    slug: row['slug'] as string,
    name: row['name'] as string,
    tagline: toNullableString(row['tagline']),
    description: toNullableString(row['description']),
    cover_image_url: toNullableString(row['banner_url']),
    teacher_name: teacherName(row['teacher']),
    classroom_count: typeof row['classroom_count'] === 'number' ? row['classroom_count'] : 0,
    student_count: typeof row['total_students'] === 'number' ? row['total_students'] : 0,
    rating: toNullableNumber(row['average_rating']),
    is_featured: row['is_featured'] as boolean,
  };
}

/** Build a one-line schedule label from a classroom's schedule type. */
function scheduleSummary(scheduleType: string): string {
  return scheduleType;
}

/** Map a guarded classroom record onto a {@link SchoolhouseClassroom}. */
function toClassroom(row: Record<string, unknown>): SchoolhouseClassroom {
  const max = row['max_students'];
  return {
    id: row['id'] as number,
    name: row['name'] as string,
    description: toNullableString(row['description']),
    max_students: typeof max === 'number' ? max : 0,
    current_enrollment: row['current_enrollment'] as number,
    schedule_summary: scheduleSummary(row['schedule_type'] as string),
  };
}

/** Map a guarded tier record onto a {@link SchoolhouseTier}. */
function toTier(row: Record<string, unknown>): SchoolhouseTier {
  const features = row['features'];
  return {
    id: row['id'] as number,
    name: row['name'] as string,
    price_monthly: toNumber(row['monthly_price']),
    features:
      Array.isArray(features) ? features.filter((f): f is string => typeof f === 'string') : [],
  };
}

/** Map a guarded enrollment record onto a {@link SchoolhouseEnrollment}. */
function toEnrollment(row: Record<string, unknown>): SchoolhouseEnrollment {
  return {
    id: row['id'] as number,
    classroom_id: row['classroom_id'] as number,
    status: row['status'] as string,
  };
}

// --- Section: Fetch helpers ---

/** Build a credentialed JSON request init, optionally cancellable. */
function jsonInit(method: 'GET' | 'POST', signal?: AbortSignal): RequestInit {
  const headers: Readonly<Record<string, string>> = { 'Content-Type': 'application/json' };
  const base: RequestInit = { method, headers, credentials: 'include' };
  return signal ? { ...base, signal } : base;
}

/**
 * Fetch and validate one schoolhouse resource, returning `null` on any gap.
 *
 * @typeParam T - The mapped view-model type.
 * @param path - Absolute `/schoolhouse/*` path.
 * @param guard - Runtime guard for one decoded record.
 * @param map - Mapper from the guarded record to the view-model.
 * @param signal - Optional abort signal so callers can cancel in-flight loads.
 * @returns The mapped value, or `null` on a non-OK or malformed response.
 * @remarks Never throws on HTTP/parse errors; callers render a fallback state.
 */
async function fetchOne<T>(
  path: string,
  guard: (value: unknown) => value is Record<string, unknown>,
  map: (row: Record<string, unknown>) => T,
  signal?: AbortSignal,
): Promise<T | null> {
  const res = await fetch(path, jsonInit('GET', signal));
  if (!res.ok) {
    return null;
  }
  const body: unknown = await res.json();
  return guard(body) ? map(body) : null;
}

/**
 * Fetch, validate, and map a JSON array of schoolhouse records.
 *
 * @typeParam T - The mapped row type.
 * @param path - Absolute `/schoolhouse/*` path returning a JSON array.
 * @param guard - Runtime guard for one row.
 * @param map - Mapper from a guarded row to the view-model.
 * @param signal - Optional abort signal.
 * @returns The mapped rows; `[]` on a non-OK, non-array, or malformed body.
 */
async function fetchList<T>(
  path: string,
  guard: (value: unknown) => value is Record<string, unknown>,
  map: (row: Record<string, unknown>) => T,
  signal?: AbortSignal,
): Promise<readonly T[]> {
  const res = await fetch(path, jsonInit('GET', signal));
  if (!res.ok) {
    return [];
  }
  const body: unknown = await res.json();
  if (!Array.isArray(body)) {
    return [];
  }
  return body.filter(guard).map(map);
}

/** Resolve the backend sort key, forcing `featured` when filtering featured. */
function resolveSortBy(params: SchoolhouseBrowseParams | undefined): SchoolhouseSortBy {
  if (params?.featuredOnly) {
    return 'featured';
  }
  return params?.sortBy ?? 'featured';
}

/** Build the browse query string from optional filters (data-driven). */
function browseQuery(params: SchoolhouseBrowseParams | undefined): string {
  const query = new URLSearchParams();
  query.set('sort_by', resolveSortBy(params));
  query.set('per_page', '50');
  if (params?.search) {
    query.set('search', params.search);
  }
  return query.toString();
}

/**
 * Fetch the active schoolhouse browse list, applying optional filters.
 *
 * @param params - Optional search/featured/sort filters.
 * @param signal - Optional abort signal so callers can cancel stale loads.
 * @returns The mapped summaries; `[]` on any non-OK or malformed response.
 * @remarks The backend has no "featured only" flag, so featured filtering is
 *   applied client-side after the featured-sorted fetch.
 */
export async function fetchSchoolhouses(
  params?: SchoolhouseBrowseParams,
  signal?: AbortSignal,
): Promise<readonly SchoolhouseSummary[]> {
  const res = await fetch(`/schoolhouse/browse?${browseQuery(params)}`, jsonInit('GET', signal));
  if (!res.ok) {
    return [];
  }
  const summaries = extractSummaries(await res.json());
  return params?.featuredOnly ? summaries.filter((s) => s.is_featured) : summaries;
}

/** Extract + map the `items` array from a guarded browse-response body. */
function extractSummaries(body: unknown): readonly SchoolhouseSummary[] {
  const items = asRecord(body)?.['items'];
  if (!Array.isArray(items)) {
    return [];
  }
  return items.filter(isPublicSchoolhouse).map(toSummary);
}

/**
 * Fetch a single schoolhouse plus its classrooms and tiers.
 *
 * @param slug - The schoolhouse slug from the route.
 * @param signal - Optional abort signal.
 * @returns The combined {@link SchoolhouseDetail}, or `null` if not found.
 * @remarks Issues the three reads concurrently; classroom/tier failures
 *   degrade to empty sections rather than failing the whole page.
 */
export async function fetchSchoolhouseDetail(
  slug: string,
  signal?: AbortSignal,
): Promise<SchoolhouseDetail | null> {
  const encoded = encodeURIComponent(slug);
  const [summary, classrooms, tiers] = await Promise.all([
    fetchOne(`/schoolhouse/${encoded}`, isPublicSchoolhouse, toSummary, signal),
    fetchList(`/schoolhouse/${encoded}/classrooms`, isClassroomRow, toClassroom, signal),
    fetchList(`/schoolhouse/${encoded}/tiers`, isTierRow, toTier, signal),
  ]);
  if (summary === null) {
    return null;
  }
  return {
    ...summary,
    classroom_count: classrooms.length,
    classrooms,
    tiers,
  };
}

/**
 * Fetch the current viewer's classroom enrollments.
 *
 * @param signal - Optional abort signal.
 * @returns The mapped enrollments; `[]` on any non-OK or malformed response.
 */
export function fetchMyEnrollments(
  signal?: AbortSignal,
): Promise<readonly SchoolhouseEnrollment[]> {
  return fetchList('/schoolhouse/enrollments/mine', isEnrollmentRow, toEnrollment, signal);
}

/**
 * Enroll the current viewer in a classroom.
 *
 * @param classroomId - The classroom to enroll in.
 * @param signal - Optional abort signal.
 * @returns The created {@link SchoolhouseEnrollment}, or `null` on failure
 *   (already enrolled, classroom full, unauthenticated, or network error).
 * @remarks Never throws; callers inspect the `null` result to show an error.
 */
export async function enrollInClassroom(
  classroomId: number,
  signal?: AbortSignal,
): Promise<SchoolhouseEnrollment | null> {
  const init = jsonInit('POST', signal);
  const res = await fetch(`/schoolhouse/classrooms/${classroomId}/enroll`, {
    ...init,
    body: JSON.stringify({ classroom_id: classroomId }),
  });
  if (!res.ok) {
    return null;
  }
  const body: unknown = await res.json();
  return isEnrollmentRow(body) ? toEnrollment(body) : null;
}
